import type {ReactElement, ReactNode} from "react";
import {forwardRef, useId} from "react";
import {Col, DSCheckbox, DSRadio, Row, Text} from "@cdx/ds";
import {WithLabel} from "./FieldWithLabel";

export type RadioGroupOption<T> = {
  value: T;
  label: ReactNode;
  desc?: string | ReactElement;
  postfix?: ReactNode;
  disabled?: boolean;
  postfixBelow?: ReactNode;
};

const ContainerComp = ({children, vertical}: {children: ReactNode; vertical?: boolean}) =>
  vertical ? (
    <Col sp="16px">{children}</Col>
  ) : (
    <Row sp="16px" wrap align="center">
      {children}
    </Row>
  );

export type RadioGroupProps<T> = {
  onChange: (value: any) => unknown;
  options: RadioGroupOption<T>[];
  name?: string;
  value: T | null;
  withDescription?: boolean;
  vertical?: boolean;
  className?: string;
  showingErrors?: boolean;
  disabled?: boolean;
};
export const RadioGroup = forwardRef<HTMLButtonElement, RadioGroupProps<any>>((props, ref) => {
  const {
    showingErrors,
    onChange,
    options,
    name,
    value,
    withDescription,
    vertical: rawVertical,
    className,
    ...rest
  } = props;

  const vertical = Boolean(rawVertical || withDescription);
  const id = useId();

  return (
    <ContainerComp vertical={vertical}>
      {options.map(
        ({value: optValue, label: optLabel, desc: optDesc, postfix, disabled, postfixBelow}, i) => {
          const labelTextId = `${id}-${optValue}-label`;
          return (
            <Row
              sp="12px"
              align={vertical && !postfix ? "start" : "center"}
              key={optValue}
              pb={postfixBelow ? "24px" : undefined}
            >
              <Row sp="8px" as="label" align="start">
                <DSRadio
                  ref={i === 0 ? ref : undefined}
                  checked={value === optValue}
                  onChange={() => onChange(optValue)}
                  disabled={rest.disabled || disabled}
                  aria-labelledby={labelTextId}
                />
                <Col sp="16px">
                  <Col sp="4px">
                    <Text type="label14" color="primary" id={labelTextId}>
                      {optLabel}
                    </Text>

                    {withDescription && optDesc && (
                      <Text type="prose14" color="secondary" maxWidth="40rem">
                        {optDesc}
                      </Text>
                    )}
                  </Col>
                  {postfixBelow}
                </Col>
              </Row>
              {postfix}
            </Row>
          );
        }
      )}
    </ContainerComp>
  );
});

type RadioGroupWithLabelProps<T> = RadioGroupProps<T> & {
  label?: string;
  hint?: string;
  hasPendingValidation?: boolean;
  showErrors?: any[];
};
/** @deprecated Use RadioGroupField / CheckboxGroupField from @cdx/common/forms-next/fields. */
export const RadioGroupWithLabel = forwardRef<HTMLButtonElement, RadioGroupWithLabelProps<any>>(
  ({showErrors, name, label, hint, hasPendingValidation, ...rest}, ref) => (
    <WithLabel
      label={label}
      name={name}
      showErrors={showErrors}
      hint={hint}
      hasPendingValidation={hasPendingValidation}
    >
      <RadioGroup name={name} {...rest} ref={ref} />
    </WithLabel>
  )
);

// value = {opt1: false, opt2: true}
// options = [{value: "opt1", label: "Option 1"}]

export type CheckboxGroupProps<T extends string> = {
  showingErrors?: boolean;
  onChange: (value: any) => void;
  options: RadioGroupOption<T>[];
  name?: string;
  value: Record<T, boolean>;
  vertical?: boolean;
  className?: string;
};

export const CheckboxGroup = forwardRef<HTMLButtonElement, CheckboxGroupProps<any>>(
  ({showingErrors, onChange, options, name, value, vertical, className, ...rest}, ref) => {
    const id = useId();
    return (
      <ContainerComp vertical={vertical}>
        {options.map(({value: optValue, label: optLabel}, i) => {
          const labelTextId = `${id}-${optValue}-label`;
          return (
            <Row sp="8px" as="label" key={optValue}>
              <DSCheckbox
                ref={i === 0 ? ref : undefined}
                checked={(value && value[optValue]) || false}
                onChange={(val) => onChange({...value, [optValue]: val})}
                {...rest}
                aria-labelledby={labelTextId}
              />
              <Text id={labelTextId} type="label14" color="primary">
                {optLabel}
              </Text>
            </Row>
          );
        })}
      </ContainerComp>
    );
  }
);

type CheckboxGroupWithLabelProps<T extends string> = CheckboxGroupProps<T> & {
  label: string;
  hint?: string;
  hasPendingValidation?: boolean;
  showErrors?: any[];
};
/** @deprecated Use RadioGroupField / CheckboxGroupField from @cdx/common/forms-next/fields. */
export const CheckboxGroupWithLabel = forwardRef<
  HTMLButtonElement,
  CheckboxGroupWithLabelProps<any>
>(({showErrors, name, label, hint, hasPendingValidation, ...rest}, ref) => (
  <WithLabel
    label={label}
    name={name}
    showErrors={showErrors}
    hint={hint}
    hasPendingValidation={hasPendingValidation}
  >
    <CheckboxGroup name={name} {...rest} ref={ref} />
  </WithLabel>
));
